import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useUserCore } from '../../../core/modules/user';
import { useGlobalCore } from '../../../core/modules/global';
import { CREATE_USER_DIALOG } from '../../../core/constants/dialog';
import { ERROR } from '../../../core/constants/errorCode';
import Spinner from '../Spinner';
import { useCompanyCore } from '../../../core/modules/company';
import { Company } from '../../../core/interfaces/company.interface';
import { USER_ROLE, USER_ROLE_LABEL, USER_ROLES } from '../../../core/constants/userRoles';
import { PostUserPayloadRequest } from '../../../core/modules/user/interfaces/post-user';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};
function getStyles(rol: number, selected: readonly number[], theme: Theme) {
  return {
    fontWeight: selected.includes(rol)
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  };
}

const schema = yup.object().shape({
  client_name: yup.string().required('Campo requerido'),
  email: yup
    .string()
    .email('Formato incorrecto')
    .required('Campo requerido'),
  password: yup.string().required('Campo requerido'),
  roles: yup.array().of(yup.number().required()).required('Campo requerido'),
  company_id: yup
    .number()
    .when("roles", {
      is: (roles: number[]) => roles.includes(USER_ROLE.SUPER_ADMIN),
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required("Campo requerido"),
    }),
});

const CreateDialog = () => {
  const theme = useTheme();
  const { dialogs, closeDialog } = useGlobalCore();
  const { companies } = useCompanyCore();
  const { errorCode, getUsers, postUser } = useUserCore();

  const [loading, setLoading]  = useState(false);
  const [roles, setRoles] = useState<number[]>([]);

  const { control, formState, handleSubmit, resetField } = useForm({
    mode: 'onChange',
    defaultValues: {
      roles: [],
    },
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  const resetForm = useCallback(() => {
    resetField('client_name');
    resetField('company_id');
    resetField('email');
    resetField('password');
    resetField('roles');
    closeDialog(CREATE_USER_DIALOG);
  }, [closeDialog, resetField]);

  const onSubmit = useCallback(
    (values: PostUserPayloadRequest) => {
      setLoading(true);
      postUser(
        { ...values, company_id: values.roles.includes(1) ? undefined : values.company_id },
        () => {
          resetForm();
          getUsers({});
          setLoading(false);
        }, () => {
          setLoading(false);
        }
      );
    },
    [postUser, resetForm, getUsers]
  );

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 w-[400px] min-w-[400px]',
      }}
      onClose={resetForm}
      maxWidth='md'
      open={Array.isArray(dialogs) && Array.isArray(dialogs) && dialogs.includes(CREATE_USER_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Crear Usuario
              </Typography>
            </div>
      
            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-24">
                    <Controller
                      name="client_name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nombre"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.client_name}
                          helperText={errors?.client_name?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>
                  <div className="mb-24">
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Email"
                          type="email"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.email}
                          helperText={errors?.email?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>
                  <div className="mb-24">
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Contraseña"
                          type="password"
                          error={!!errors.password}
                          helperText={errors?.password?.message}
                          variant="outlined"
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>
                  <div className="mb-24">
                    <FormControl className="flex" variant="outlined" required>
                      <InputLabel id="user-role-label">Rol</InputLabel>
                      <Controller
                        name="roles"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId="user-roles-label"
                            id="user-roles-label"
                            multiple
                            onChange={(ev) => {
                              field.onChange(ev);
                              setRoles(ev.target.value as any);
                            }}
                            disabled={loading}
                            input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                  <Chip key={value} label={USER_ROLE_LABEL[value]} />
                                ))}
                              </Box>
                            )}
                            MenuProps={MenuProps}
                            required
                          >
                            {USER_ROLES.map(({ name, value }) => (
                              <MenuItem
                                key={value}
                                value={value}
                                style={getStyles(value, field.value, theme)}
                              >
                                {name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  </div>

                  {!Array.isArray(roles) ? null : !roles.includes(USER_ROLE.SUPER_ADMIN) && (
                    <div className="mb-24">
                      <FormControl className="flex" variant="outlined">
                        <InputLabel id="user-company-label">Compañia *</InputLabel>
                        <Controller
                          name="company_id"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              labelId="user-company-label"
                              disabled={loading}
                              label="Compañia *"
                            >
                              {companies.map((company: Company) => (
                                <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                      </FormControl>
                    </div>
                  )}

                  {errorCode === ERROR.ERR_400 && (
                    <Typography color="error" className="text-center mb-32 text-md">
                      Ya existe un usuario con ese email
                    </Typography>
                  )}

                  <div className="flex justify-center">
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ borderRadius: 50 }}
                      disabled={!isEmpty(errors) || !isValid || loading}
                      type="submit"
                      size="large"
                    >
                      <Spinner show={loading} />
                      Guardar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDialog;
